import React, {useEffect, useRef, useState} from 'react';
import styles from './VideoEditorUI.module.css';
import PageTitle from '../../../uiToolkit/PageTitle';
import {GeneratedVideoChunk} from '../../../data/models/LibraryFile/GeneratedVideoChunk';
// import TipBox from '../../../uiToolkit/TipBox';
import Timeline from '../components/Timeline';
import {EditChunkOption} from '../../../data/repositories/LibraryRepository';
import {VideoEditorText} from '../VideoEditorText';
// import FormattedText from '../../../uiToolkit/FormattedText/FormattedText';
import ChunkList from '../components/ChunkList';
import ChunkPreview from '../components/ChunkPreview';
import {VideoEditorState} from '../store/VideoEditorStore';
import BackButton from '../../../uiToolkit/Buttons/BackButton';
import ChunkListOverlay from '../components/ChunkListOverlay';
import Button from '../../../uiToolkit/Buttons/Button';
import {appText} from '../../../assets/text/appText';
import ErrorBox2 from '../../../uiToolkit/ErrorBox2/ErrorBox2';

interface Props {
  onBack: () => void;
  chunks: GeneratedVideoChunk[];
  selectedChunk?: GeneratedVideoChunk;
  isLoading: boolean;
  spinTrigger: number;
  state: VideoEditorState;
  hasUnsavedChanges?: boolean;
  onRevertUnsavedChanges?: () => void;
  onSaveUnsavedChanges?: () => void;
  onEditChunk: (option: EditChunkOption, chunkId: string) => void;
  refreshChunks: () => void;
  editChunkErrorCode?: string;
  clearErrors: () => void;
  onChunksReorder: (
    chunks: GeneratedVideoChunk[],
    activeChunkId: string,
    newIndex: number,
  ) => void;
  onSelectChunk: (chunk: GeneratedVideoChunk) => void;
}

const EditVideoSourceUI: React.FC<Props> = ({
  onBack,
  chunks,
  selectedChunk,
  isLoading,
  spinTrigger,
  state,
  hasUnsavedChanges,
  onRevertUnsavedChanges,
  onSaveUnsavedChanges,
  onEditChunk,
  refreshChunks,
  editChunkErrorCode,
  clearErrors,
  onChunksReorder,
  onSelectChunk,
}: Props) => {
  const defaultPixelsPerSecond = 5;
  const divRef = useRef<HTMLDivElement | null>(null);

  const [totalTimelineSeconds, setTotalTimelineSeconds] = useState(0);
  const [pixelsPerSecond, setPixelsPerSecond] = useState(
    defaultPixelsPerSecond,
  );
  const [stepInSeconds, setStepInSeconds] = useState(30);
  const [intervals, setIntervals] = useState(6);
  const hasPendingChunkOperations = chunks.some(
    chunk => chunk.fileStatus !== 0,
  );
  const saveActionsDisabled =
    hasPendingChunkOperations ||
    (!hasUnsavedChanges && state === VideoEditorState.Idle);

  useEffect(() => {
    calibrateZoom();
    const handleResize = () => {
      calibrateZoom();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [chunks]);

  const calibrateZoom = () => {
    const total = chunks.reduce((sum, x) => sum + x.duration, 0);
    setTotalTimelineSeconds(total);

    if (divRef.current && chunks.length > 0) {
      const rect = divRef.current.getBoundingClientRect();
      const clientRectWidth = rect.width;

      if (total > 0) {
        if (clientRectWidth >= total * defaultPixelsPerSecond * 4) {
          // Третий режим (сверхзум)
          setPixelsPerSecond(20);
          setStepInSeconds(5);
          setIntervals(5);
        } else if (clientRectWidth >= total * defaultPixelsPerSecond * 2) {
          // Double zoom
          setPixelsPerSecond(10);
          setStepInSeconds(15);
          setIntervals(3);
        } else {
          // Обычный режим
          setPixelsPerSecond(5);
          setStepInSeconds(30);
          setIntervals(6);
        }
      }
    }
  };

  return (
    <div ref={divRef} className={styles.videoEditorContainer}>
      <PageTitle title={VideoEditorText.title} />
      <div className={styles.headLine}>
        <BackButton overrideOnBack={onBack} />
        <div className={styles.saveResetButtons}>
          <Button
            title={appText.revertChanges}
            uiType="text"
            disabled={saveActionsDisabled}
            onClick={() => onRevertUnsavedChanges && onRevertUnsavedChanges()}
          />
          <Button
            title={appText.saveChanges}
            disabled={saveActionsDisabled}
            onClick={() => onSaveUnsavedChanges && onSaveUnsavedChanges()}
          />
        </div>
      </div>
      <ChunkPreview selectedChunk={selectedChunk} />
      <ErrorBox2
        errorCodes={editChunkErrorCode ? [editChunkErrorCode] : []}
        clearErrors={clearErrors}
      />
      {hasUnsavedChanges && (
        <div className={styles.unsavedChangesMessageContainer}>
          <p>{appText.unsavedChangesMessage}</p>
        </div>
      )}
      <div className={styles.chunksOverlay}>
        <div
          className={`${styles.chunksContainer} ${
            state !== VideoEditorState.Idle ? styles.busy : ''
          }`}>
          <Timeline
            endTime={totalTimelineSeconds}
            step={stepInSeconds}
            pixelsPerSecond={pixelsPerSecond}
            intervals={intervals}
          />
          <ChunkList
            chunks={chunks}
            selectedChunk={selectedChunk}
            onEditChunk={onEditChunk}
            onSelectChunk={onSelectChunk}
            onChunksReorder={onChunksReorder}
            totalTimelineSeconds={totalTimelineSeconds}
            pixelsPerSecond={pixelsPerSecond}
          />
        </div>
        <ChunkListOverlay
          state={state}
          isLoading={isLoading}
          spinTrigger={spinTrigger}
          refreshChunks={refreshChunks}
        />
      </div>
      {/* <TipBox
        noStandardHeader={true}
        tip={
          <>
            <div className="tipHeader">{VideoEditorText.tipHeader}</div>
            <div className="tipContent">
              <ul>
                <li>
                  <FormattedText text={VideoEditorText.tip1} />
                </li>
                <li>
                  <FormattedText text={VideoEditorText.tip2} />
                </li>
                <li>
                  <FormattedText text={VideoEditorText.tip3} />
                </li>
                <li>
                  <FormattedText text={VideoEditorText.tip4} />
                </li>
              </ul>
            </div>
          </>
        }
      /> */}
    </div>
  );
};

export default EditVideoSourceUI;
